import { withApollo } from '../lib/apollo'
import { AuthenticatedUser, AuthProvider } from '../lib/authContext'
import { PermissionProvider } from '../lib/permissionContext'
import { useState, useEffect, useRef, useCallback } from 'react'
import { getUserFromToken, getDecodedToken, getAccessTokenFromToken } from '../lib/token'
import { isCurrentVersion, missingVersion, setToCurrentVersion } from '../lib/version'
import { useRouter } from 'next/router'
import AppLayout from '../components/AppLayout'
import { ThemeProvider } from 'styled-components'
import theme from '../lib/theme';
import '../styles/globals.css'
import { CableProvider, SocketStatus } from '../lib/cableContext'
import { AppProps } from 'next/app'

const emptyUser : AuthenticatedUser = {
  id: null,
  name: null,
  email: null,
  roles: [],
  franchise: {
    id: null,
    name: null,
    logo: null,
    motto: null
  },
  league_id: null,
}

function App({pageProps, Component}: AppProps) : JSX.Element {
  const [user, setUser] = useState<AuthenticatedUser>(emptyUser)
  const [token, setToken] = useState<string>('')
  const [authenticated, setAuthenticated] = useState(false)
  const [socket, setSocket] = useState<WebSocket|null>(null)
  const [socketStatus, setSocketStatus] = useState<SocketStatus>('disconnected')
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const router = useRouter()

  const login = () : void => {
    setUser(getUserFromToken())
    setToken(getAccessTokenFromToken())
    setAuthenticated(true)
  }

  const logout = () : void => {
    setUser(emptyUser)
    setAuthenticated(false)
  }

  const isAllowedTo = (permission:string, franchiseId?:number) : boolean => {
    if (franchiseId) {
      if(parseInt(user.franchise.id || '0') !== franchiseId) {
        return false
      } else {
        return true
      }
    }

    return (user && user.roles?.includes(permission))
  }

  const checkVersionNumber = () : void => {
    if(missingVersion()) {
      console.log("set missing version")
      setToCurrentVersion();
    }

    if(!isCurrentVersion()) {
      console.log("version mismatch")
      logout();
      router.push("/login")
    }
  }

  const connectSocket = useCallback(() : void => {
    // Cancel any pending auto-reconnect
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    // Clean up the previous socket so we don't leak listeners
    if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
      socketRef.current.onclose = null // prevent re-triggering reconnect from old socket
      socketRef.current.close()
    }

    const cableUrl = process.env.NEXT_PUBLIC_SOCKET_URL ?? 'ws://localhost:5000/cable'
    const cable = new WebSocket(`${cableUrl}?token=${token}`)
    socketRef.current = cable
    setSocketStatus('connecting')

    cable.onopen = () => {
      setSocketStatus('connected')
      cable.send(JSON.stringify({
        command: 'subscribe',
        identifier: JSON.stringify({
          channel: 'NotificationsChannel',
          league_id: '1'
        })
      }))

      // cable.send(JSON.stringify({
      //   command: 'subscribe',
      //   identifier: JSON.stringify({
      //     channel: 'AuctionChannel',
      //     auction_id: '1'
      //   })
      // }))
    }

    cable.onclose = () => {
      setSocketStatus('disconnected')
      // Auto-reconnect after 3 seconds
      reconnectTimerRef.current = setTimeout(() => {
        if (token) connectSocket()
      }, 3000)
    }

    cable.onerror = () => {
      setSocketStatus('disconnected')
    }

    setSocket(cable)
  }, [token])

  useEffect(() => {
    if(!authenticated) {
      if(getDecodedToken()) {
        login();
        checkVersionNumber();
      }
      else {
        router.push("/login")
      }
    }
  }, []);

  useEffect(() => {
    if(token) {
      connectSocket()
    }
  }, [token])

  return (
    <AuthProvider value={{user: user, authenticated: authenticated, accessToken: token, login: login, logout: logout}}>
      <CableProvider value={{socket: socket, socketStatus: socketStatus, reconnect: connectSocket}}>
        <PermissionProvider value={{isAllowedTo: isAllowedTo}}>
          <ThemeProvider theme={theme}>
            <AppLayout>
              <Component {...pageProps} />
            </AppLayout>
          </ThemeProvider>
        </PermissionProvider>
      </CableProvider>
    </AuthProvider>
  )
}

export default withApollo()(App);
